/* ══════════════════ Jungle Mode — Type Definitions ══════════════════ */

export type RouteDirection = 'top-to-bot' | 'bot-to-top';
export type AIStrategy = 'mirror' | 'top-to-bot' | 'bot-to-top' | 'random';

/** User-configurable assumptions for jungle mode */
export interface JungleModeConfig {
  playerSide: 'blue' | 'red';
  routeDirection: RouteDirection;
  aiStrategy: AIStrategy;
  /** Game time (seconds) when the full clear finishes, e.g. 167 = 2:47 */
  clearFinishTime: number;
  /** Game time (seconds) to arrive at scuttle crab after clear. 0 = no scuttle. */
  scuttleTime: number;
  /** Seconds per camp on 2nd+ clears (default 10) */
  subsequentClearTime: number;
  /** Champion movement speed in game units/sec (default 350) */
  moveSpeed: number;
  /** Seconds to recall + heal at base (default 7) */
  baseTime: number;
  /** Game time (seconds) at which jungle mode starts (default 55 = 0:55) */
  startTime: number;
}

export const DEFAULT_CONFIG: JungleModeConfig = {
  playerSide: 'blue',
  routeDirection: 'top-to-bot',
  aiStrategy: 'mirror',
  clearFinishTime: 167, // 2:47
  scuttleTime: 180, // 3:00
  subsequentClearTime: 10,
  moveSpeed: 350,
  baseTime: 7,
  startTime: 55,
};

/** A single row in the decision table */
export interface JungleModeStep {
  stepNumber: number;
  campId: string;
  campName: string;
  walkTime: number;
  clearTime: number;
  /** Game time when arriving at the camp (before clearing) */
  gameTimeArrive: number;
  /** Game time after clearing the camp */
  gameTimeAfter: number;
  /** How long the camp was sitting alive before we arrived (0 if we arrived as it spawned or walked to dead camp) */
  idleTime: number;
  gold: number;
  xp: number;
  cumulativeGold: number;
  cumulativeXP: number;
  level: number;
  cycleNumber: number;
  /** If true, this is a "free time" separator row, not a camp clear */
  isFreeTimeRow: boolean;
  /** Seconds of free time (only set on separator rows) */
  freeTime?: number;
  /** Description for free time row, e.g. "Base 7s + walk 22s to Gromp" */
  freeTimeDetail?: string;
}

/** Saved state for undo — everything needed to restore to a previous point */
export interface JungleModeSnapshot {
  gameTime: number;
  playerPosition: { gx: number; gy: number };
  aiPosition: { gx: number; gy: number };
  playerRouteIndex: number;
  aiRouteIndex: number;
  playerCycle: number;
  aiCycle: number;
  cumulativeGold: number;
  cumulativeXP: number;
  aiCumulativeGold: number;
  aiCumulativeXP: number;
  /** Deep copy of camp runtime states */
  campStates: Array<{
    id: string;
    alive: boolean;
    nextSpawnAt: number;
    killedAt: number | null;
  }>;
  steps: JungleModeStep[];
  /** Saved wave counters for consistent restore */
  waveCounter: { blue: number; red: number };
  nextWaveTime: { blue: number; red: number };
  nextMinionId: number;
  campKills: Record<string, number>;
}
